import React, { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map, Marker } from 'maplibre-gl';
import { Select, SelectItem, Input, Link, Button, CircularProgress, CheckboxGroup, Checkbox } from "@heroui/react";
import { FaCircle } from "react-icons/fa";
import {Textarea} from "@heroui/input";
import { AreaData, getAreas, getRecords } from '../api/api';
import { renderAreas } from '../util/areas';
import { RecordKind, Gender } from '../api/api';
import { ColorCodingMode, getMarkerProps } from '../util/marker';
import { RecordKindIcon } from './RecordKindIcon';


interface MarkerData {
    ID: number;
    RecordKind: RecordKind;
    Alias: string;
    Gender: Gender;
    AgeRange: string;
    LamplightCaseNumber: string | null;
    SAVOLO: string | null;
    ASP: string | null;
    Business: string | null;
    IdentifiableMarkers: string | null;
    CaseTag: [];
    InteractionLength: string | null;
    ReferralFrom: string | null;
    ReferralTo: string | null;
    Notes: string | null;
    Location: [number, number]; // [Latitude, Longitude]
    CreatedAt: string; // ISO 8601 timestamp
    LastUpdated: string; // ISO 8601 timestamp
    ReportedBy: string;
    ReportedAt: string;
}


function getStats(markersData: MarkerData[], key: keyof MarkerData) {
    const counts: Record<string, number> = {}
    for (const m of markersData) {
        const mKey = m[key] as string
        counts[mKey] = (counts[mKey] | 0) + 1;
    }
    const entries = Object.entries(counts);
    entries.sort((a, b) => b[1] - a[1]);
    return entries.map(([k, v]) => ({ [key]: k, count: v }));
}


const MapMain: React.FC = () => {

    const mapContainer = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<Map | null>(null); // To reference the map instance
    const [mapIsLoaded, setMapIsLoaded] = useState(false);

    const markersOnMap = useRef<Marker[]>([]); // To store active markers on the map
    const [markersData, setMarkersData] = useState<MarkerData[]>([]); // State to store fetched markers

    const [areasData, setAreasData] = useState<AreaData[]>([]); // State to store fetched areas

    const [isCollapsed, setIsCollapsed] = useState(true); // To manage the collapse state of the filter section
    const [isCollapsedColourCoding, setIsCollapsedColourCoding] = useState(true); // To manage the collapse state of the colour coding section

    const [selectedAlias, setSelectedAlias] = useState<Set<string>>(new Set());
    const [selectedLamplightCaseNumber, setSelectedLamplightCaseNumber] = useState<Set<string>>(new Set());
    const [selectedReporter, setSelectedReporter] = useState<Set<string>>(new Set());
    const [selectedDateRange, setSelectedDateRange] = useState<string>('lastmonth');

    const [selectedRecordKinds, setSelectedRecordKinds] = useState<RecordKind[]>(['person', 'business', 'location']);

    const [colorCoding, setColorCoding] = useState<ColorCodingMode>('gender'); // Default setting for Colour Coding

    const [showAreas, setShowAreas] = useState(true);

    const [selectedMarkerData, setSelectedMarkerData] = useState<MarkerData | null>(null); // To store the selected marker data

    const detailsContainerRef = useRef<HTMLDivElement | null>(null);

    const [loadingCounter, setLoadingCounter] = useState(0);

    const toTitleCase = (str: string): string =>
        str && str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

    const handleClickOutside = (event: MouseEvent) => {
        if (
            detailsContainerRef.current &&
            !detailsContainerRef.current.contains(event.target as Node) &&
            selectedMarkerData
        ) {
            closeDetails();
        }
    };

    const aliasOptions = useMemo(() => getStats(markersData, "Alias"), [markersData]);
    const lamplightCaseNumOptions = useMemo(() => getStats(markersData, "LamplightCaseNumber"), [markersData]);
    const reportedByOptions = useMemo(() => getStats(markersData, "ReportedBy"), [markersData]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedMarkerData]);

    const fetchMarkers = async () => {
        setLoadingCounter(v => v + 1);
    
        try {
            const allRecords = await getRecords(selectedDateRange);

            // Normalize and map the data to MarkerData format
            const data: MarkerData[] = allRecords.map((record: any) => ({
                ID: record.id,
                RecordKind: record.record_kind,
                Alias: record.alias,
                Gender: record.gender || 'other',
                AgeRange: record.age_range,
                LamplightCaseNumber: record.lamplight_case_number,
                SAVOLO: record.savolo ? toTitleCase(record.savolo.toString()) : null,
                ASP: record.asp ? toTitleCase(record.asp.toString()) : null,
                Business: record.business,
                IdentifiableMarkers: record.identifiable_markers,
                CaseTag: record.case_tag, // Convert stringified array to an actual array
                InteractionLength: record.length_of_interaction ? toTitleCase(record.length_of_interaction.toString()) : null,
                ReferralFrom: record.referral_from ? toTitleCase(record.referral_from.toString()) : null,
                ReferralTo: record.referral_to ? toTitleCase(record.referral_to.toString()) : null,
                Notes: record.notes || null,
                Location: record.location, // Convert stringified location array to an actual array
                CreatedAt: new Date(record.created_at).toISOString(), // Format date to ISO string
                LastUpdated: new Date(record.last_updated).toISOString(), // Format date to ISO string
                ReportedBy: record.reported_by,
                ReportedAt: new Date(record.reported_at || record.created_at).toISOString(), // Format date to ISO string
            }));
    
            setMarkersData(data); // Update state with parsed markers
        } catch (error) {
            console.error("Error fetching markers:", error);
        } finally {
            setLoadingCounter(v => v - 1);
        }
    };

    const fetchAreas = async () => {
        setLoadingCounter( v=> v+1);
        try {    
            setAreasData(await getAreas(false));
        } catch (error) {
            console.error("Error fetching areas:", error);
        } finally {
            setLoadingCounter(v => v-1);
        }
    };
    

    useEffect(() => {
        fetchMarkers();
    }, [selectedDateRange]);


    useEffect(() => {
        const map = new maplibregl.Map({
            container: mapContainer.current as HTMLElement,
            style: 'https://tiles.openfreemap.org/styles/liberty', // OpenFreeMap style
            center: [-3.1883, 55.9533], // Edinburgh City Center [Longitude, Latitude]
            zoom: 14,
            attributionControl: false
        });

        // Set default attribution control to closed
        map.addControl(new maplibregl.AttributionControl({ compact: false }));

        map.on('load', () => setMapIsLoaded(true));

        mapRef.current = map;

        fetchMarkers();
        fetchAreas();

        return () => {
            map.remove();
            markersOnMap.current = [];
        };
    }, []); // Re-fetch markers when the token updates

    const filteredMarkers = useMemo(() => {
        if (!markersData){
            return [];
        }

        return markersData.filter((marker) => {
            let isMatch = true;
    
            // Check alias filter
            if (selectedAlias?.size) {
                isMatch = isMatch && selectedAlias.has(marker.Alias);
            }

            // Check lamplight case number filter
            if (selectedLamplightCaseNumber?.size) {
                isMatch = isMatch && selectedLamplightCaseNumber.has(marker.LamplightCaseNumber?.toString() as string);
            }

            // Check lamplight case number filter
            if (selectedReporter?.size) {
                isMatch = isMatch && selectedReporter.has(marker.ReportedBy?.toString() as string);
            }

            // Check date range filter
            if (selectedDateRange && selectedDateRange !== 'All') {
                const createdDate = new Date(marker.CreatedAt);
                const now = new Date(); // Current date and time
                switch (selectedDateRange) {
                    case 'lastweek':
                        isMatch = isMatch && createdDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // Subtract 7 days;
                        break;
                    case 'lastmonth':
                        isMatch = isMatch && createdDate >= new Date(new Date().setMonth(now.getMonth() - 1));
                        break;
                    case 'last3months':
                        isMatch = isMatch && createdDate >= new Date(new Date().setMonth(now.getMonth() - 3));
                        break;
                    case 'last6months':
                        isMatch = isMatch && createdDate >= new Date(new Date().setMonth(now.getMonth() - 6));
                        break;
                    case 'last12months':
                        isMatch = isMatch && createdDate >= new Date(new Date().setFullYear(now.getFullYear() - 1));
                        break;
                    default:
                        break;
                }
            }

            // Check record kind filter 
            isMatch = isMatch && (selectedRecordKinds.includes(marker.RecordKind) || !marker.RecordKind);

            return isMatch;
        });


    }, [markersData, selectedDateRange, selectedAlias, selectedLamplightCaseNumber, selectedReporter, selectedRecordKinds]);


    useEffect(() => {
        // Update the map with the filtered markers
        if(!mapIsLoaded){
            return;
        }
        markersOnMap.current.forEach((marker) => marker.remove());
        markersOnMap.current = [];
    
        filteredMarkers.forEach((marker) => {
            const markerInstance = new maplibregl.Marker(getMarkerProps(marker, colorCoding))
                .setLngLat([marker.Location[1], marker.Location[0]])
                .addTo(mapRef.current as Map);
    
            // Add click listener to update selected marker data
            markerInstance.getElement().addEventListener('click', () => {
                setSelectedMarkerData(marker);
            });
    
            markersOnMap.current.push(markerInstance);
        });
    }, [filteredMarkers, mapIsLoaded, colorCoding]);

    useEffect(() => {
        mapIsLoaded && renderAreas(areasData, showAreas, mapRef.current as maplibregl.Map)
    }, [mapIsLoaded, areasData, showAreas]);


    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleColourCodingCollapse = () => {
        setIsCollapsedColourCoding(!isCollapsedColourCoding);
    };

    const closeDetails = () => {
        setSelectedMarkerData(null);
    };

    return (
        <div className="w-full h-full">
            {/* Collapsible Filter Section */}
            <div ref={mapContainer} className="w-full h-full">
            <div className="absolute top-0 right-0 z-10 bg-white shadow-md rounded-lg">
                {loadingCounter ? (
                    <div className='absolute top-0 right-0 z-100 w-full h-full'
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    >
                        <CircularProgress label="Loading..." />
                    </div>
                ) : <></>}

                <div
                    style={styles.filterHeader}
                    onClick={toggleCollapse}
                >
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Filters</span>
                </div>

                {!isCollapsed && (
                    <div style={styles.filterContent}>
                        <CheckboxGroup 
                            orientation="horizontal"
                            value={selectedRecordKinds as string[]}
                            onValueChange={setSelectedRecordKinds as any}>
                            <Checkbox value="person"><RecordKindIcon recordKind='person' size={20}/></Checkbox>
                            <Checkbox value="business"><RecordKindIcon recordKind='business' size={20}/></Checkbox>
                            <Checkbox value="location"><RecordKindIcon recordKind='location' size={20}/></Checkbox>
                        </CheckboxGroup>
                        <Select
                            label="Alias"
                            name="alias"
                            selectionMode='multiple'
                            isClearable={true}
                            selectedKeys={selectedAlias}
                            onSelectionChange={setSelectedAlias as any}
                        >
                            {aliasOptions.map(({Alias, count}) => <SelectItem key={Alias} textValue={Alias as string}>{Alias} [{count}]</SelectItem>)}
                        </Select>

                        <Select
                             label="Lamplight Case Number"
                            name="lamplightcasenumber"
                            selectionMode='multiple'
                            isClearable={true}
                            selectedKeys={selectedLamplightCaseNumber}
                            onSelectionChange={setSelectedLamplightCaseNumber as any}
                        >
                            {lamplightCaseNumOptions.map(({LamplightCaseNumber, count}) => <SelectItem  textValue={LamplightCaseNumber.toString()} key={LamplightCaseNumber.toString()}>{LamplightCaseNumber} [{count}]</SelectItem>)}
                        </Select>

                        <Select
                            label="Reported By"
                            name="reportedby"
                            selectionMode='multiple'
                            isClearable={true}
                            selectedKeys={selectedReporter}
                            onSelectionChange={setSelectedReporter as any}
                        >
                            {reportedByOptions.map(({ReportedBy, count}) => <SelectItem  textValue={ReportedBy as string} key={ReportedBy}>{ReportedBy} [{count}]</SelectItem>)}
                        </Select>

                        <Select
                            label="Date Range"
                            placeholder="Select a range"
                            value={selectedDateRange}
                            onChange={(e) => setSelectedDateRange(e.target.value)}
                            defaultSelectedKeys={[selectedDateRange]}
                        >
                            <SelectItem key="lastweek" textValue="Last Week">Last Week</SelectItem>
                            <SelectItem key="lastmonth" textValue="Last Month">Last Month</SelectItem>
                            <SelectItem key="last3months" textValue="Last 3 Months">Last 3 Months</SelectItem>
                            <SelectItem key="last6months" textValue="Last 6 Months">Last 6 Months</SelectItem>
                            <SelectItem key="last12months" textValue="Last 12 Months">Last 12 Months</SelectItem>
                        </Select>

                        <div className="p-2">
                            <Checkbox isSelected={showAreas} onValueChange={setShowAreas}>Areas</Checkbox>
                        </div>

                    </div>
                )}
                <div
                    style={styles.filterHeader}
                    onClick={toggleColourCodingCollapse}
                >
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Colours</span>
                </div>
                {!isCollapsedColourCoding && (
                    <div style={styles.filterContent}>
                        <Select
                            aria-label="Select a color coding option"
                            value={colorCoding} // Bind to the colorCoding state
                            onChange={(e) => setColorCoding(e.target.value as ColorCodingMode)} // Update colorCoding state
                            defaultSelectedKeys={[colorCoding]}
                            style={{ width: 'fit-content', minWidth: '150px' }}
                        >
                            <SelectItem key="gender" textValue="Gender">Gender</SelectItem>
                            <SelectItem key="age_range" textValue="Age Range">Age Range</SelectItem>
                            <SelectItem key="savolo" textValue="Savolo">Savolo</SelectItem>
                            {/* Add more options as needed */}
                        </Select>

                        <div style={{ marginTop: '5px', textAlign: 'left', width: '100%' }}>
                            {/* <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Legend:</p> */}
                            {colorCoding === 'gender' && (
                                <ul>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#0000FF', fontWeight: 'bold' }}><FaCircle /></span> Male</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FF1493', fontWeight: 'bold' }}><FaCircle /></span> Female</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#a02dcaff', fontWeight: 'bold' }}><FaCircle /></span> Other</li>
                                </ul>
                            )}
                            {colorCoding === 'age_range' && (
                                <ul>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FF0000', fontWeight: 'bold' }}><FaCircle /></span> 0-25</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FFA500', fontWeight: 'bold' }}><FaCircle /></span> 26-49</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FFFF00', fontWeight: 'bold' }}><FaCircle /></span> 50+</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#008000', fontWeight: 'bold' }}><FaCircle /></span> Unknown</li>
                                </ul>
                            )}
                            {colorCoding === 'savolo' && (
                                <ul>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FF0000', fontWeight: 'bold' }}><FaCircle /></span> Yes</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#008000', fontWeight: 'bold' }}><FaCircle /></span> No</li>
                                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px'}}><span style={{ color: '#FFFF00', fontWeight: 'bold' }}><FaCircle /></span> Unknown</li>
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>
            </div>
            <div
                ref={detailsContainerRef}
                className="max-w-(--breakpoint-md)"
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%', // Position relative to the center
                    transform: 'translateX(-50%)', // Center horizontally by shifting half of the width
                    width: '90%',
                    height: '60%',
                    backgroundColor: '#fff',
                    boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    overflowY: 'auto',
                    padding: '15px',
                    display: selectedMarkerData ? 'block' : 'none',
                    marginBottom: '40px',
                }}
            >
                <button
                    onClick={closeDetails}
                    style={{
                        position: 'absolute',
                        top: '0px',
                        right: '0px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                    }}
                >
                    X
                </button>
                {selectedMarkerData && (
                    <div className="font-inter text-lg">
                        <div className="flex justify-between items-center">
                            <Button 
                                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600"
                                as={Link} 
                                href={`/record/${selectedMarkerData.ID}`}
                            >
                                View Record
                            </Button>
                            {selectedMarkerData.RecordKind}
                            <Button 
                                className="bg-red-500 text-white font-semibold py-2 px-4 rounded hover:bg-red-600"
                                onPress={closeDetails}
                            >
                                Close
                            </Button>
                        </div>
                        <div className="my-4">
                            <Textarea 
                                label="Reported At"
                                labelPlacement="inside"
                                value={new Date(selectedMarkerData.ReportedAt).toLocaleString("en-GB")}
                                isReadOnly
                                size="lg"
                                minRows={1}
                            />
                        </div>
                        <div className="my-4">
                            <Input
                                label="Alias"
                                value={selectedMarkerData.Alias}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                        { selectedMarkerData.RecordKind == 'person' ?
                            <div className="flex gap-2 my-4">
                                <Input
                                    label="Gender"
                                    value={toTitleCase(selectedMarkerData.Gender)}
                                    isReadOnly
                                    size="lg"
                                />
                                <Input
                                    label="Age Range"
                                    value={selectedMarkerData.AgeRange}
                                    isReadOnly
                                    size="lg"
                                />
                            </div>
                         : <></>}
                        <div className="my-4">
                            <Input
                                label="Case Tags"
                                value={selectedMarkerData.CaseTag?.join(', ')}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                        <div className="flex gap-2 my-4">
                            <Input
                                label="Interaction Time"
                                value={selectedMarkerData.InteractionLength ?? undefined}
                                isReadOnly
                                size="lg"
                            />
                             { selectedMarkerData.RecordKind == 'person' ? 
                                <Input
                                    label="Lamplight No"
                                    value={selectedMarkerData.LamplightCaseNumber ?? undefined}
                                    isReadOnly
                                    size="lg"/>
                                : <></>}
                        </div>
                        { selectedMarkerData.RecordKind == 'person' ? <>
                            <div className="flex gap-2 my-4">
                                <Input
                                    label="SAVOLO"
                                    value={selectedMarkerData.SAVOLO ?? undefined}
                                    isReadOnly
                                    size="lg"
                                />
                                <Input
                                    label="ASP"
                                    value={selectedMarkerData.ASP ?? undefined}
                                    isReadOnly
                                    size="lg"
                                />
                            </div>
                            <div className="my-4">
                                <Input
                                    label="Identifiable Markers"
                                    value={selectedMarkerData.IdentifiableMarkers ?? undefined}
                                    isReadOnly
                                    size="lg"
                                />
                            </div>
                          </>  : <></>}
                        <div className="my-4">
                            <Input
                                label="Referral From"
                                value={selectedMarkerData.ReferralFrom ?? undefined}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                        <div className="my-4">
                            <Input
                                label="Referral To"
                                value={selectedMarkerData.ReferralTo ?? undefined}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                        <div className="my-4">
                            <Input
                                label="Notes"
                                value={selectedMarkerData.Notes ?? undefined}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                        <div className="my-4">
                            <Input
                                label="Reporter"
                                value={selectedMarkerData.ReportedBy}
                                isReadOnly
                                size="lg"
                            />
                        </div>
                    <div className='text-xs text-gray-500'>
                        { selectedMarkerData.CreatedAt == selectedMarkerData.ReportedAt ? <></> : <>Created {new Date(selectedMarkerData.CreatedAt).toLocaleString("en-GB")}</>}
                        { selectedMarkerData.CreatedAt != selectedMarkerData.ReportedAt && selectedMarkerData.LastUpdated != selectedMarkerData.CreatedAt ? <br/> : <></>}
                        { selectedMarkerData.LastUpdated == selectedMarkerData.CreatedAt ? <></> : <>Updated {new Date(selectedMarkerData.LastUpdated).toLocaleString("en-GB")}</> }
                    </div>
                    </div>

                )}
            </div>

        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    filterBar: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        backgroundColor: '#fff',
        padding: '0px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
        minWidth: '200px',
        maxWidth: '300px',
    },
    filterHeader: {
        padding: '5px',
        backgroundColor: '#00819e',
        color: '#fff',
        cursor: 'pointer',
        borderRadius: '0px',
        textAlign: 'center',
    },
    filterContent: {
        marginTop: '5px',
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        minWidth: '200px',
        gap: '5px',
        alignItems: 'center', // Centers children horizontally
    }
};

export default MapMain;
